import { API_BASE_URL } from "../../../config/apiConfig";
// AirportSearch.jsx - Backend-only version optimized for your API
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Plane, X, Loader, AlertCircle, RotateCcw, Clock, Zap } from 'lucide-react';

// Format airport display text
const formatAirportDisplay = (airport) => {
  if (!airport) return '';
  if (airport.code && airport.city) {
    return `${airport.city} (${airport.code}) - ${airport.name}`;
  } else if (airport.name && airport.code) {
    return `${airport.name} (${airport.code})`;
  }
  return airport.name || 'Unknown Airport';
};

// Extract 3-letter airport code from string
const extractAirportCode = (airportString) => {
  if (!airportString) return '';
  
  if (typeof airportString === 'string' && airportString.length === 3 && /^[A-Z]{3}$/.test(airportString)) {
    return airportString.toUpperCase();
  }
  
  const match = airportString.match(/\(([A-Z]{3})\)/);
  if (match && match[1]) {
    return match[1].toUpperCase();
  }
  
  return '';
};

// Get country name from country code
const getCountryName = (countryCode) => {
  const countries = {
    'PK': 'Pakistan',
    'EG': 'Egypt',
    'US': 'United States',
    'GB': 'United Kingdom',
    'AE': 'United Arab Emirates',
    'SA': 'Saudi Arabia',
    'TR': 'Turkey',
    // Add more as needed
  };
  return countries[countryCode] || countryCode;
};

const AirportSearch = ({ 
  value, 
  onChange, 
  placeholder = "Search city or airport...",
  countryCode = null,
  showToast,
  label = "Airport",
  token = null,
  apiEndpoint = `${API_BASE_URL}/discover/airports`
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [searchTime, setSearchTime] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  
  // Refs
  const searchTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('airport_recent_searches');
      if (saved) {
        setRecentSearches(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading recent searches:', e);
    }
  }, []);

  // Initialize with existing value if provided
  useEffect(() => {
    if (value && value.trim()) {
      setSearchQuery(value);
    }
  }, [value]);

  // Save to recent searches
  const saveToRecentSearches = useCallback((airport) => {
    try {
      const newRecent = [
        airport,
        ...recentSearches.filter(item => item.code !== airport.code)
      ].slice(0, 5);
      setRecentSearches(newRecent);
      localStorage.setItem('airport_recent_searches', JSON.stringify(newRecent));
    } catch (e) {
      console.error('Error saving recent search:', e);
    }
  }, [recentSearches]);

  // Search airports via backend API
  const searchAirports = useCallback(async (query) => {
    const normalizedQuery = String(query).trim();
    if (normalizedQuery.length < 2) {
      setAirports([]);
      setError(null);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const startTime = performance.now();
    setLoading(true);
    setError(null);

    try {
      // Check if token is available
      if (!token) {
        throw new Error('Authentication required');
      }

      // Call backend API
      const response = await fetch(
        apiEndpoint,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            cityName: normalizedQuery,
            countryCode: countryCode || null
          }),
          signal: abortControllerRef.current.signal
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`Search failed (${response.status})`);
      }

      const data = await response.json();
      
      // Handle the response format from your API
      // Based on your example: [{ name, code, city, country, countryCode, source }]
      let results = [];
      if (Array.isArray(data)) {
        results = data;
      } else if (data.data && Array.isArray(data.data)) {
        results = data.data;
      } else if (data.airports && Array.isArray(data.airports)) {
        results = data.airports;
      }

      // Transform to consistent format (though your API already has good format)
      const transformedResults = results
        .map(airport => ({
          code: airport.code || '',
          city: airport.city || '',
          name: airport.name || '',
          country: getCountryName(airport.country) || airport.country || '',
          countryCode: airport.countryCode || airport.country || '',
          source: airport.source || 'api'
        }))
        .filter(airport => airport.code && airport.code.length === 3)
        .slice(0, 10);

      if (transformedResults.length === 0) {
        setError(`No airports found for "${normalizedQuery}"`);
        setAirports([]);
      } else {
        setAirports(transformedResults);
      }

      setSearchTime(performance.now() - startTime);

    } catch (err) {
      if (err.name === 'AbortError') {
        return;
      }
      
      console.error('Search error:', err);
      setError(err.message || 'Failed to search airports');
      setAirports([]);
    } finally {
      setLoading(false);
    }
  }, [token, countryCode, apiEndpoint]);

  // Debounced search
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        searchAirports(searchQuery);
      }, 400); // Slightly longer debounce for API calls
      
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    } else {
      setAirports([]);
      setError(null);
      setShowDropdown(false);
    }
  }, [searchQuery, searchAirports]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handlers
  const handleAirportSelect = (airport) => {
    const displayValue = formatAirportDisplay(airport);
    onChange(displayValue);
    setSearchQuery(displayValue);
    setShowDropdown(false);
    setError(null);
    setIsResetMode(false);
    saveToRecentSearches(airport);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    setIsResetMode(false);
    
    if (newValue.trim().length >= 2) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleInputFocus = () => {
    if (searchQuery.trim().length >= 2) {
      setShowDropdown(true);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setAirports([]);
    setError(null);
    setIsResetMode(true);
    setShowDropdown(true);
    onChange('');
    
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const handleContainerClick = () => {
    if (!showDropdown && searchQuery.trim().length >= 2) {
      setShowDropdown(true);
      searchAirports(searchQuery);
    }
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className="w-full relative" ref={wrapperRef}>
      <div 
        className="relative cursor-text"
        onClick={handleContainerClick}
      >
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
          <Plane size={18} className="text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={isResetMode ? "Search for a new city..." : placeholder}
          className={`w-full pl-10 pr-20 py-3 border-2 border-gray-200 
                   rounded-xl focus:outline-none focus:border-blue-500 
                   focus:ring-2 focus:ring-blue-200 transition-all
                   cursor-text ${!token ? 'bg-gray-50' : 'bg-white'}`}
          disabled={!token}
        />
        
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1 z-10">
          {loading ? (
            <Loader size={18} className="text-blue-500 animate-spin" />
          ) : searchQuery ? (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                type="button"
                title="Clear and search new city"
              >
                <RotateCcw size={16} className="text-blue-500" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery('');
                  onChange('');
                }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                type="button"
                title="Clear completely"
              >
                <X size={16} className="text-gray-400" />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {/* Reset Mode Hint */}
      {isResetMode && (
        <div className="absolute -bottom-6 left-0 text-xs text-blue-500 flex items-center gap-1">
          <Search size={12} />
          Type to search for a new city
        </div>
      )}

      {/* No Token Warning */}
      {!token && (
        <div className="absolute -bottom-6 left-0 text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          Please login to search airports
        </div>
      )}

      {/* Dropdown Results */}
      {showDropdown && token && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 
                      rounded-xl shadow-lg max-h-96 overflow-y-auto">
          
          {/* Recent Searches */}
          {recentSearches.length > 0 && searchQuery.length < 2 && (
            <div className="border-b border-gray-200">
              <div className="px-4 py-2 bg-gray-50 flex items-center justify-between sticky top-0">
                <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                  <Clock size={12} />
                  Recent Searches
                </span>
                <button
                  onClick={() => {
                    setRecentSearches([]);
                    localStorage.removeItem('airport_recent_searches');
                  }}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Clear
                </button>
              </div>
              {recentSearches.map((airport, index) => (
                <button
                  key={`recent-${airport.code}-${index}`}
                  onClick={() => handleAirportSelect(airport)}
                  className="w-full p-3 text-left hover:bg-blue-50 transition-colors 
                           border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                >
                  <div className="w-8 h-8 flex items-center justify-center 
                                bg-amber-100 rounded-lg flex-shrink-0">
                    <Clock size={14} className="text-amber-600" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-gray-900 truncate">
                      {airport.city || airport.name} ({airport.code})
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {airport.name} • {airport.country}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {/* Loading State */}
          {loading && (
            <div className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Loader size={16} className="text-blue-500 animate-spin" />
                <span className="text-sm text-gray-600">
                  Searching airports...
                </span>
              </div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                  <div className="w-8 h-8 bg-gray-200 rounded-lg flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Error State */}
          {error && !loading && (
            <div className="p-4 text-center">
              <AlertCircle size={20} className="text-red-500 mx-auto mb-2" />
              <p className="text-red-600 text-sm">{error}</p>
              <p className="text-gray-400 text-xs mt-1">
                Try again or check your search term
              </p>
            </div>
          )}
          
          {/* Airport Results */}
          {airports.length > 0 && !loading && (
            <>
              {/* Performance Header */}
              <div className="sticky top-0 px-4 py-2 bg-gray-50 border-b border-gray-200 
                            flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
                  <Plane size={12} />
                  Search Results
                </span>
                {searchTime > 0 && (
                  <span className="text-xs text-gray-500 flex items-center gap-1">
                    <Zap size={10} className={searchTime < 500 ? "text-green-500" : "text-amber-500"} />
                    {Math.round(searchTime)}ms
                  </span>
                )}
              </div>
              
              {/* Airport List */}
              {airports.map((airport, index) => (
                <button
                  key={`${airport.code}-${index}`}
                  onClick={() => handleAirportSelect(airport)}
                  className="w-full p-3 text-left hover:bg-blue-50 transition-colors 
                           border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                >
                  <div className="w-8 h-8 flex items-center justify-center bg-blue-100 rounded-lg flex-shrink-0">
                    <Plane size={14} className="text-blue-600" />
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                      {airport.city || airport.name} ({airport.code})
                      {airport.source === 'local' && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                          Verified
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {airport.name} • {airport.country}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}
          
          {/* No Results State */}
          {!error && !loading && airports.length === 0 && searchQuery.length >= 2 && (
            <div className="p-4 text-center">
              <Search size={24} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600">No airports found for "{searchQuery}"</p>
              <p className="text-gray-400 text-xs mt-1">
                Try a different city name or airport code
              </p>
            </div>
          )}
          
          {/* Initial State */}
          {!error && !loading && airports.length === 0 && searchQuery.length < 2 && (
            <div className="p-4 text-center">
              <Search size={24} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600 text-sm">
                {isResetMode ? 'Start typing to search' : `Search for ${label.toLowerCase()}`}
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Enter at least 2 characters
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AirportSearch;

export { formatAirportDisplay, extractAirportCode };